import { useCallback, useState } from "react";
import { useRevealDownload } from "@/hooks/useRevealDownload";
import {
  ToolContainer,
  FileDropZone,
  ProcessingState,
  downloadBlob,
} from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import JSZip from "jszip";
import { Download } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";

// Reference table of common platform asset dimensions. Kept as a simple,
// easy-to-update array (not hardcoded logic) since these specs change
// periodically as platforms update their guidelines.
const PRESETS = [
  { key: "ig-post", label: "Instagram Post", w: 1080, h: 1080 },
  { key: "ig-portrait", label: "Instagram Portrait (4:5)", w: 1080, h: 1350 },
  { key: "ig-story", label: "Instagram Story / Reel", w: 1080, h: 1920 },
  { key: "fb-post", label: "Facebook Post", w: 1200, h: 630 },
  { key: "fb-cover", label: "Facebook Cover", w: 851, h: 315 },
  { key: "fb-story", label: "Facebook Story", w: 1080, h: 1920 },
  { key: "x-header", label: "X (Twitter) Header", w: 1500, h: 500 },
  { key: "x-post", label: "X (Twitter) Post", w: 1600, h: 900 },
  { key: "li-post", label: "LinkedIn Post", w: 1200, h: 627 },
  { key: "li-banner", label: "LinkedIn Banner", w: 1584, h: 396 },
  { key: "pin", label: "Pinterest Pin", w: 1000, h: 1500 },
  { key: "tiktok", label: "TikTok Video Cover", w: 1080, h: 1920 },
  { key: "yt-banner", label: "YouTube Channel Banner", w: 2560, h: 1440 },
  { key: "yt-thumb", label: "YouTube Thumbnail", w: 1280, h: 720 },
] as const;

interface ResizedAsset {
  key: string;
  label: string;
  blob: Blob;
  url: string;
  focus: FocusPoint;
}

type FocusPoint = "top-left" | "top" | "top-right" | "left" | "center" | "right" | "bottom-left" | "bottom" | "bottom-right";

const FOCUS_POINTS: { value: FocusPoint; label: string; x: number; y: number }[] = [
  { value: "top-left", label: "↖", x: 0, y: 0 },
  { value: "top", label: "↑", x: 0.5, y: 0 },
  { value: "top-right", label: "↗", x: 1, y: 0 },
  { value: "left", label: "←", x: 0, y: 0.5 },
  { value: "center", label: "•", x: 0.5, y: 0.5 },
  { value: "right", label: "→", x: 1, y: 0.5 },
  { value: "bottom-left", label: "↙", x: 0, y: 1 },
  { value: "bottom", label: "↓", x: 0.5, y: 1 },
  { value: "bottom-right", label: "↘", x: 1, y: 1 },
];

async function resizeCover(
  file: File,
  targetW: number,
  targetH: number,
  focus: { x: number; y: number }
): Promise<Blob> {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await new Promise(resolve => (img.onload = resolve));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d")!;

  // "cover" fit: scale to fill target, crop overflow. Which part of the
  // overflow gets cropped away is controlled by the focus point (0,0 =
  // keep the top-left, 1,1 = keep the bottom-right, 0.5,0.5 = centered)
  // instead of always being centered regardless of what's actually in
  // the image.
  const scale = Math.max(
    targetW / img.naturalWidth,
    targetH / img.naturalHeight
  );
  const scaledW = img.naturalWidth * scale;
  const scaledH = img.naturalHeight * scale;
  const offsetX = (targetW - scaledW) * focus.x;
  const offsetY = (targetH - scaledH) * focus.y;

  ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);

  return new Promise(resolve => canvas.toBlob(b => resolve(b!), "image/png"));
}

const TOOL_INFO_CONTENT = {
  what: `Resizes a single image into the correct dimensions for every major social platform at once — Instagram, Facebook, X, LinkedIn, Pinterest, and YouTube — instead of manually re-exporting for each one.`,
  how: `Upload your image, choose a crop focus point if your subject isn't centered, select which platform sizes you need, and generate. Download each sized version individually or all together as a ZIP.`,
  tip: `The tool crops to fill each target size, since every platform expects a different aspect ratio — use the focus point picker to control which part of your image stays in frame if your main subject isn't centered, rather than risk it getting cropped out on the platforms with the most different shape (like a square Instagram post versus a wide Facebook cover).`,
};

export default function SocialMediaFormatter() {
  const [file, setFile] = useState<File | null>(null);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(PRESETS.map(p => p.key))
  );
  const [focusPoint, setFocusPoint] = useState<FocusPoint>("center");
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<ResizedAsset[]>([]);
  const reveal = useRevealDownload(results.length > 0);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setResults([]);
  }, []);

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const generate = async () => {
    if (!file || selected.size === 0) return;
    setProcessing(true);
    try {
      const targets = PRESETS.filter(p => selected.has(p.key));
      const focus = FOCUS_POINTS.find(f => f.value === focusPoint) ?? FOCUS_POINTS[4];
      const assets: ResizedAsset[] = [];
      for (const preset of targets) {
        const blob = await resizeCover(file, preset.w, preset.h, { x: focus.x, y: focus.y });
        assets.push({
          key: preset.key,
          label: preset.label,
          blob,
          url: URL.createObjectURL(blob),
          focus: focusPoint,
        });
      }
      setResults(assets);
      toast.success(`Generated ${assets.length} sizes`);
    } catch {
      toast.error("Failed to generate images");
    } finally {
      setProcessing(false);
    }
  };

  // Regenerates just one already-generated size with a different focus
  // point, leaving every other size untouched -- previously the only
  // way to fix one badly-cropped size was to change the global focus
  // point and regenerate everything, which could just as easily break a
  // different size that was already fine.
  const regenerateOne = async (key: string, newFocus: FocusPoint) => {
    if (!file) return;
    const preset = PRESETS.find(p => p.key === key);
    if (!preset) return;
    const focus = FOCUS_POINTS.find(f => f.value === newFocus) ?? FOCUS_POINTS[4];
    const blob = await resizeCover(file, preset.w, preset.h, { x: focus.x, y: focus.y });
    setResults(prev =>
      prev.map(a => (a.key === key ? { ...a, blob, url: URL.createObjectURL(blob), focus: newFocus } : a))
    );
    setEditingKey(null);
    toast.success(`Updated ${preset.label}`);
  };

  // Every file leaves through downloadBlob, which asks the usage gate (Professional Tools: the 10 a day pool) and
  // takes the visit's one use only when the file is saved. At 0 remaining the paywall opens and nothing is saved.
  const downloadAll = async () => {
    const zip = new JSZip();
    for (const asset of results) {
      zip.file(`${asset.key}.png`, asset.blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    downloadBlob(content, "social-media-assets.zip");
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload
          accept="image/*"
          onFiles={handleFiles}
          label="Drop an image to reformat for every platform"
          description="Upload once, get correctly-sized versions for each platform"
        />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex items-center justify-between">
            <p className="text-sm font-medium">{file.name}</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFile(null);
                setResults([]);
              }}
            >
              Change image
            </Button>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Crop focus point</p>
            <p className="text-xs text-muted-foreground mb-2">
              Every size below crops to fill its target shape — choose which part of your image stays in frame
              across all of them, rather than always keeping the center.
            </p>
            <div className="grid grid-cols-3 gap-1.5 w-32">
              {FOCUS_POINTS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => { setFocusPoint(f.value); setResults([]); }}
                  className={`h-9 w-9 rounded-lg border text-base flex items-center justify-center transition-colors ${
                    focusPoint === f.value ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground"
                  }`}
                  title={f.value.replace("-", " ")}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Choose sizes to generate</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggle(p.key)}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    selected.has(p.key)
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card"
                  }`}
                >
                  <p className="font-medium">{p.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.w} × {p.h}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {processing ? (
            <ProcessingState />
          ) : (
            <Button onClick={generate} disabled={selected.size === 0}>
              Generate {selected.size} Size{selected.size === 1 ? "" : "s"}
            </Button>
          )}

          {results.length > 0 && (
            <div className="space-y-3">
              <div ref={reveal.ref} className={`inline-block ${reveal.className}`}>
                <Button variant="outline" onClick={downloadAll}>
                  <Download className="h-4 w-4 mr-2" /> Download All as ZIP
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {results.map(asset => (
                  <Card key={asset.key} className="p-3">
                    <img
                      src={asset.url}
                      alt={asset.label}
                      className="w-full rounded mb-2 border"
                    />
                    <p className="text-xs font-medium">{asset.label}</p>
                    <div className="flex items-center justify-between mt-1">
                      <button
                        type="button"
                        onClick={() => downloadBlob(asset.blob, `${asset.key}.png`)}
                        className="text-xs text-primary hover:underline"
                      >
                        Download
                      </button>
                      <button
                        className="text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setEditingKey(editingKey === asset.key ? null : asset.key)}
                      >
                        {editingKey === asset.key ? "Close" : "Edit crop"}
                      </button>
                    </div>
                    {editingKey === asset.key && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1.5">Adjust just this size's crop:</p>
                        <div className="grid grid-cols-3 gap-1">
                          {FOCUS_POINTS.map(f => (
                            <button
                              key={f.value}
                              type="button"
                              onClick={() => regenerateOne(asset.key, f.value)}
                              className={`h-7 w-7 rounded border text-xs flex items-center justify-center transition-colors ${
                                asset.focus === f.value ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground"
                              }`}
                              title={f.value.replace("-", " ")}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
